import useFetchData from 'hooks/useFetch';
import React, { useRef } from 'react';
import { GetGroupDetailsByIdServices } from '../services/chatServices';
import { useTranslation } from 'react-i18next';
import UserShortCut from 'components/common/UserShortCut';
import { useMediaContext } from 'contexts/MediaContext';
import { getIconByExtension } from 'utils/globalMethodes';
import { GroupDetailsSkeleton } from './ChatSkeletons';
interface DetailsGroupProps {
  groupId: number;
  setShowGroupDetail: React.Dispatch<React.SetStateAction<boolean | null>>;
  showGroupDetail?: boolean | null;
}
const DetailsGroup: React.FC<DetailsGroupProps> = ({
  groupId,
  setShowGroupDetail,
  showGroupDetail,
}) => {
  const { data: response } = useFetchData(GetGroupDetailsByIdServices, [
    groupId,
  ]);
  const data = response?.success ? response.data : null;
  const { t } = useTranslation();
  const containerDocument = useRef<HTMLDivElement | null>(null);
  const { openMedia } = useMediaContext();

  const handelShowFile = async (
    title: string,
    id: number,
    mimetype: string,
  ): Promise<void> => {
    const url = `/upload/GetChatDocument/${id}`;

    if (mimetype === 'application/pdf' || mimetype.startsWith('image/')) {
      openMedia(url, mimetype.startsWith('image/') ? 'img' : 'pdf', title);
    } else {
      const link = document.createElement('a');
      link.href = url;
      link.download = title;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handelScroll = (dir: string): void => {
    if (!containerDocument.current) return;

    const scrollAmount = 200;
    const currentScroll = containerDocument.current.scrollLeft;

    if (dir === 'left') {
      containerDocument.current.scrollTo({
        left: currentScroll - scrollAmount,
        behavior: 'smooth',
      });
    } else if (dir === 'right') {
      containerDocument.current.scrollTo({
        left: currentScroll + scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  if (!data)
    return (
      <div
        className={`tw-h-full tw-overflow-y-auto tw-bg-[rgba(248,250,252,0.7)] ${showGroupDetail ? '' : 'tw-hidden'}`}
      >
        <GroupDetailsSkeleton />
      </div>
    );
  return (
    <div
      className={`tw-h-full tw-overflow-y-auto tw-bg-[rgba(248,250,252,0.7)] ${showGroupDetail ? '' : 'minthr-hide'}`}
    >
      {/* Top bar with back */}
      <div className="tw-shrink-0 tw-flex tw-items-center tw-px-2.5 tw-py-1.5 tw-border-b tw-border-gray3 tw-bg-white">
        <button
          className="back-to-chat tw-flex tw-items-center tw-justify-center tw-w-9 tw-h-9 tw-rounded-full hover:tw-bg-light-gray3 tw-border-none tw-bg-transparent tw-cursor-pointer tw-transition"
          onClick={() => setShowGroupDetail(null)}
        >
          <img
            className="tw-w-3.5 tw-h-3.5 tw-icon-gray tw-rotate-180"
            src="/icons/arrow.svg"
            alt=""
          />
        </button>
      </div>

      {/* Header / Avatar */}
      <div className="tw-text-center tw-px-6 tw-py-6 tw-border-b tw-border-gray3">
        <div className="tw-mx-auto tw-w-24 tw-h-24 tw-rounded-3xl tw-overflow-hidden tw-bg-primary-opacity">
          <img
            className="tw-w-full tw-h-full tw-object-cover"
            src={`/upload/profileimages/${data.id}/${data.imagePath}`}
            alt=""
          />
        </div>

        <h1 className="tw-mt-4 tw-mb-2 tw-text-2xl tw-font-semibold tw-text-dark">
          {data.name}
        </h1>

        <div className="tw-text-xs tw-text-dark-gray3 tw-mb-3">
          {data.groupCreator.fullName} {t('Chat.group.created.on')}{' '}
          {data.groupCreator.creationDate}
        </div>

        <p className="tw-text-sm tw-text-dark tw-m-0">{data.description}</p>
      </div>

      {/* Admins / Members */}
      <div className="tw-flex tw-gap-6 tw-px-6 tw-py-5 tw-border-b tw-border-gray3">
        <div className="tw-flex-1">
          <h5 className="tw-text-sm tw-font-semibold tw-text-dark tw-mb-3">
            {t('chat.admins')}
            <span className="tw-text-primary tw-ml-1">
              {data.admins.length}
            </span>
          </h5>

          <div className="tw-flex tw-items-center">
            {data.admins.slice(0, 3).map((item, index) => (
              <UserShortCut
                key={index}
                className="minthr-avatar tw-w-9 tw-h-9 tw-rounded-full tw-border-2 tw-border-white tw--mr-2 tw-max-w-max first:tw-ml-0"
                src={`upload/profileimages/${item.id}/${item.birthday}`}
                alt=""
              />
            ))}
            {data.admins.length > 3 && (
              <span className="tw-w-9 tw-h-9 tw-rounded-full tw-border-2 tw-border-white tw--ml-2 tw-bg-light-gray3 tw-text-dark tw-text-xs tw-font-semibold tw-flex tw-items-center tw-justify-center">
                +{data.admins.length - 3}
              </span>
            )}
          </div>
        </div>

        <div className="tw-flex-1">
          <h5 className="tw-text-sm tw-font-semibold tw-text-dark tw-mb-3">
            {t('chat.members')}
            <span className="tw-text-primary tw-ml-1">
              {data.members.length}
            </span>
          </h5>
          <div className="tw-flex tw-items-center">
            {data.members.slice(0, 3).map((item, index) => (
              <UserShortCut
                key={index}
                className="minthr-avatar tw-w-9 tw-h-9 tw-rounded-full tw-border-2 tw-border-white tw--mr-2 tw-max-w-max first:tw-ml-0"
                src={`upload/profileimages/${item.id}/${item.birthday}`}
                alt=""
              />
            ))}
            {data.members.length > 3 && (
              <span className="tw-w-9 tw-h-9 tw-rounded-full tw-border-2 tw-border-white tw--ml-2 tw-bg-light-gray3 tw-text-dark tw-text-xs tw-font-semibold tw-flex tw-items-center tw-justify-center">
                +{data.members.length - 3}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Documents */}
      <div className="tw-px-6 tw-py-5">
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
          <h5 className="tw-text-sm tw-font-semibold tw-text-dark tw-m-0">
            {t('Documents')}{' '}
            <span className="tw-text-primary">{data.documents.length}</span>
          </h5>
          <div className="tw-flex tw-items-center tw-gap-2">
            <button
              onClick={() => handelScroll('left')}
              className="minthr-chat-documents-slider-prev tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-full tw-bg-light-gray2 hover:tw-bg-light-gray3 tw-border-none tw-transition"
            >
              <img
                className="tw-w-3 tw-h-3 tw-icon-gray minthr-icon-reverse"
                src="/icons/arrow.svg"
                alt=""
              />
            </button>
            <button
              onClick={() => handelScroll('right')}
              className="minthr-chat-documents-slider-next tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-full tw-bg-light-gray2 hover:tw-bg-light-gray3 tw-border-none tw-transition"
            >
              <img
                className="tw-w-3 tw-h-3 tw-icon-gray"
                src="/icons/arrow.svg"
                alt=""
              />
            </button>
          </div>
        </div>

        <div
          ref={containerDocument}
          className="tw-flex tw-gap-3 tw-overflow-x-auto tw-pb-2 tw-custom-scrollbar"
        >
          {data.documents.map((document, index) => (
            <div
              key={index}
              className="tw-shrink-0 tw-w-[calc(var(--unit-minthr)*140)] tw-rounded-2xl tw-bg-light-gray2 tw-p-2 DownLoadFile"
            >
              <a
                onClick={() => {
                  handelShowFile(document.name, document.id, document.mimetype);
                }}
                className="tw-block tw-cursor-pointer tw-no-underline"
              >
                <div className="tw-w-full tw-h-[calc(var(--unit-minthr)*100)] tw-rounded-xl tw-overflow-hidden tw-bg-white tw-flex tw-items-center tw-justify-center">
                  {document.mimetype.startsWith('image/') ? (
                    <img
                      className="tw-w-full tw-h-full tw-object-cover"
                      src={`/upload/GetChatDocument/${document.id}`}
                      alt=""
                    />
                  ) : (
                    <img
                      className="tw-w-10 tw-h-10"
                      src={`/icons/${getIconByExtension(document.path)}`}
                      alt=""
                    />
                  )}
                </div>
                <div className="tw-mt-2 tw-text-xs tw-text-dark tw-truncate">
                  {document.name}
                </div>
              </a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DetailsGroup;
